import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';

import { BlogHomeElementDataType } from '@/components/sections/builder/elements/body/blog-home/type';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import BackgroundSetting from '@/components/sections/builder/settings/background-setting';
import ColorPicker from '@/components/commons/color-picker';
import { ColorResult } from 'react-color';
import { Checkbox } from '@/components/ui/checkbox';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import { defaultData } from '@/components/sections/builder/elements/body/blog-post';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as BlogHomeElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Header</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Content Alignment</h4>
        <InputSelect
          defaultValue={data.contentAlign || defaultData.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />

      <div className='my-4 flex flex-col gap-2'>
        <label>Container color scheme</label>
        <SchemeSetting
          value={data.colorSchemeBlogHome}
          onChange={(theme) => {
            builder.setElementData(element.id, {
              colorSchemeBlogHome: theme,
            });
          }}
        />
      </div>
      <BackgroundSetting
        value={data.background}
        isShowBackgroundLayout={false}
        onChange={(background) =>
          builder.setElementData(element.id, {
            background,
          })
        }
      />
      {data?.background?.type !== 'image' && (
        <>
          <div className='mb-4 mt-4 font-bold'>Overlay</div>
          <ColorPicker
            color={data.overlay}
            inputColor={data.overlay}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) =>
              builder.setElementData(element.id, { overlay: e.target.value })
            }
            onChangePercentage={(val) =>
              builder.setElementData(element.id, { overlayPercentage: val })
            }
          />
        </>
      )}
      <div className='mt-4 flex gap-x-1'>
        <Checkbox
          id='enableCarousel'
          className='mr-2'
          checked={data.enableCarousel}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableCarousel: !!checked,
            });
          }}
        />
        <label htmlFor='enableCarousel'>Enable Carousel</label>
      </div>
      {data.enableCarousel && (
        <>
          <div className='mt-4 flex gap-x-1'>
            <Checkbox
              id='autoPlay'
              className='mr-2'
              checked={data.autoplay}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  autoplay: !!checked,
                });
              }}
            />
            <label htmlFor='autoPlay'>Autoplay slides</label>
          </div>
          <InputSlider
            label='Change slides every'
            value={data.changeSlidesEvery as number}
            max={9}
            min={1}
            onChange={(val) =>
              builder.setElementData(element.id, {
                changeSlidesEvery: val,
              })
            }
          />
        </>
      )}
    </div>
  );
}
